import { fetchBot } from '@/services/services';
import { ProviderRole } from '@bariendo/lib/constants';
import { EMPTY_IVR_ROUTE_AVAILABILITY, IvrRouteAvailability } from '@bariendo/lib/ivr-route-test';
import { getOperationOutcomeMessage } from '@bariendo/lib/error';
import { useDebouncedValue } from '@mantine/hooks';
import { useMedplum } from '@medplum/react';
import { useEffect, useMemo, useState } from 'react';

export const IVR_ROUTE_AVAILABILITY_BOT = 'ivr-route-availability';

export interface IvrRouteAvailabilityQuery {
  /** `Patient/<id>` of the simulated caller; absent for an unknown number. */
  patient?: string;
  /** Roles any chain in the form references. */
  roles: ProviderRole[];
  /** `Practitioner/<id>` for every `specific-provider` rung in the form. */
  providers: string[];
  at: Date;
}

export interface IvrRouteAvailabilityState {
  availability: IvrRouteAvailability;
  loading: boolean;
  error: string | undefined;
}

/**
 * The availability snapshot behind the route tester (BAR-2624).
 *
 * Keyed on caller, time and the roles in play — never on the order of the rungs — so
 * rearranging a chain re-resolves the path from the snapshot already in hand, with no
 * request. Nothing here writes: the bot's membership policy is read-only.
 */
export function useIvrRouteAvailability(query: IvrRouteAvailabilityQuery): IvrRouteAvailabilityState {
  const medplum = useMedplum();
  const [availability, setAvailability] = useState<IvrRouteAvailability>(EMPTY_IVR_ROUTE_AVAILABILITY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const roles = [...query.roles].sort().join(',');
  const providers = [...query.providers].sort().join(',');
  const at = query.at.toISOString();
  const patient = query.patient ?? '';

  // One scalar key, so a re-render that rebuilds the arrays does not refetch, and dragging
  // the time picker does not fire a request per keystroke.
  const [debouncedKey] = useDebouncedValue(`${patient}|${at}|${roles}|${providers}`, 400);

  const request = useMemo(() => {
    const [patientKey, atKey, rolesKey, providersKey] = debouncedKey.split('|');
    return {
      patient: patientKey || undefined,
      at: atKey,
      roles: rolesKey ? (rolesKey.split(',') as ProviderRole[]) : [],
      providers: providersKey ? providersKey.split(',') : [],
    };
  }, [debouncedKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchBot<IvrRouteAvailability>(medplum, IVR_ROUTE_AVAILABILITY_BOT, request)
      .then((result) => {
        if (cancelled) {
          return;
        }
        setAvailability(result);
        setError(undefined);
      })
      .catch((reason) => {
        if (cancelled) {
          return;
        }
        // Cleared, not left stale: a snapshot from the previous caller would report
        // availability for the wrong person.
        setAvailability(EMPTY_IVR_ROUTE_AVAILABILITY);
        setError(getOperationOutcomeMessage(reason));
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, request]);

  return { availability, loading, error };
}
